/**
 * Layout Anchor Mode — Split-screen PDF ↔ Map anchor placement
 *
 * Left pane: PDF viewer with pan/zoom, clickable for PDF-side anchor placement
 * Right pane: MapCanvas (existing, in parent)
 *
 * Anchor flow:
 *   1. Click on PDF → red crosshair at PDF coordinates
 *   2. Click on map → green marker at map coordinates
 *   3. AnchorPair created → transform recomputes automatically
 *   4. Labels appear on map as transform improves
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import { useUIStore } from '../../stores/uiStore';
import { solveTransform } from '../../domain/layout/transform';
import { dispatchPlanBake, companionLink, syncCompanionPlacement } from '../../utils/planBakeDispatch';
import { refitDxfRegistration } from '../../hooks/useDxfIngestPoller';
import type { AnchorPair } from '../../domain/layout/types';
import './LayoutAnchorMode.css';

export default function LayoutAnchorMode() {
  const siteLayout = useProjectStore((s) => s.data.siteLayout);
  const updateAnchors = useProjectStore((s) => s.updateSiteLayoutAnchors);
  const setLayoutAnchorMode = useUIStore((s) => s.setLayoutAnchorMode);
  const addNotification = useUIStore((s) => s.addNotification);

  // PDF viewer state
  const canvasRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  // Anchor placement state
  const [pendingPdfPoint, setPendingPdfPoint] = useState<{ x: number; y: number } | null>(null);
  const [waitingForMapClick, setWaitingForMapClick] = useState(false);

  const anchors = siteLayout?.anchors ?? [];
  const pageImage = siteLayout?.pageImage;

  // ── Fit image on mount ──

  useEffect(() => {
    if (!canvasRef.current || !pageImage) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = rect.width / pageImage.widthPx;
    const scaleY = rect.height / pageImage.heightPx;
    const fitZoom = Math.min(scaleX, scaleY) * 0.95;
    setZoom(fitZoom);
    setPan({
      x: (rect.width - pageImage.widthPx * fitZoom) / 2,
      y: (rect.height - pageImage.heightPx * fitZoom) / 2,
    });
  }, [pageImage]);

  // ── Register map click handler ──

  useEffect(() => {
    if (!waitingForMapClick) return;

    const handleMapClick = (e: CustomEvent<{ lng: number; lat: number }>) => {
      if (!pendingPdfPoint || !siteLayout) return;

      const newAnchor: AnchorPair = {
        id: `anchor-${Date.now()}`,
        pdfX: pendingPdfPoint.x,
        pdfY: pendingPdfPoint.y,
        mapLng: e.detail.lng,
        mapLat: e.detail.lat,
      };

      const updatedAnchors = [...anchors, newAnchor];
      const transform = solveTransform(updatedAnchors);
      updateAnchors(updatedAnchors, transform ?? undefined);

      setPendingPdfPoint(null);
      setWaitingForMapClick(false);

      addNotification({
        type: 'success',
        title: `Anchor ${updatedAnchors.length} placed`,
        message: transform
          ? `RMSE: ${transform.residualMeters.toFixed(1)}m`
          : 'Need at least 2 anchors for transform',
        duration: 3000,
      });
    };

    window.addEventListener('layout-anchor-map-click', handleMapClick as EventListener);
    return () => window.removeEventListener('layout-anchor-map-click', handleMapClick as EventListener);
  }, [waitingForMapClick, pendingPdfPoint, anchors, siteLayout, updateAnchors, addNotification]);

  // ── PDF click → anchor placement ──

  const handlePdfClick = useCallback((e: React.MouseEvent) => {
    if (isDragging || !canvasRef.current || !pageImage || !siteLayout) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Convert screen coords to image pixel coords
    const imgX = (clickX - pan.x) / zoom;
    const imgY = (clickY - pan.y) / zoom;

    // Convert image pixels to PDF points
    const pdfX = (imgX / pageImage.widthPx) * pageImage.pageWidthPt;
    // PDF origin is bottom-left, image origin is top-left
    const pdfY = (1 - imgY / pageImage.heightPx) * pageImage.pageHeightPt;

    setPendingPdfPoint({ x: pdfX, y: pdfY });
    setWaitingForMapClick(true);
  }, [isDragging, pan, zoom, pageImage, siteLayout]);

  // ── Pan/zoom handlers ──

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return; // left click only
    dragStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    setIsDragging(false);

    const handleMove = (me: MouseEvent) => {
      const dx = me.clientX - dragStartRef.current.x;
      const dy = me.clientY - dragStartRef.current.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) setIsDragging(true);
      setPan({
        x: dragStartRef.current.panX + dx,
        y: dragStartRef.current.panY + dy,
      });
    };

    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      setTimeout(() => setIsDragging(false), 10);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  }, [pan]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    const newZoom = Math.min(Math.max(zoom * factor, 0.1), 10);

    // Zoom toward mouse position
    setPan({
      x: mouseX - (mouseX - pan.x) * (newZoom / zoom),
      y: mouseY - (mouseY - pan.y) * (newZoom / zoom),
    });
    setZoom(newZoom);
  }, [zoom, pan]);

  // ── Delete anchor ──

  const handleDeleteAnchor = useCallback((id: string) => {
    const updated = anchors.filter((a) => a.id !== id);
    const transform = updated.length >= 2 ? solveTransform(updated) : null;
    updateAnchors(updated, transform ?? undefined);
  }, [anchors, updateAnchors]);

  // ── Done ──

  const handleDone = useCallback(() => {
    setLayoutAnchorMode(false);
    // WP-A converged path: closing the anchor session IS the "placement is
    // right" declaration, so the companion plan bakes itself — asking for a
    // Bake click after anchoring would be registering twice (owner). Only
    // fires when this layout auto-created its plan (planOverlayId), only
    // when the bake is missing or placement-stale, and never while one is
    // already generating — a Done after a micro-refinement re-bakes, which
    // is exactly what a changed placement needs (the previousBaked rollback
    // guards a failed attempt). The old separate plan flow stays manual.
    void (async () => {
      const link = companionLink(useProjectStore.getState().data.siteLayout);
      // A ghost link used to return here indistinguishably from "this layout
      // has no plan", so a user who finished anchoring saw nothing happen and
      // had no way to learn why. Name it, and name the gesture that fixes it.
      if (link.state === 'ghost') {
        addNotification({
          type: 'warning',
          title: 'Plan sheet is missing',
          message: 'This layout is linked to a plan that is no longer on the site, so nothing was placed or baked. Import the sheet from the Plans Layout ticket — it will re-link and place itself.',
        });
        return;
      }
      if (link.state === 'none') return;
      const overlay = link.overlay;
      // Re-derive the sheet's placement here too: the per-anchor sync misses
      // an overlay whose async creation finished AFTER the last anchor click
      // (the WP-A smoke race) — Done is also the repair gesture for that.
      // AWAITED and durable: the bake below must capture the verified-final
      // placement, not race a bounds write that might roll back.
      await syncCompanionPlacement();
      try {
        const outcome = await dispatchPlanBake(overlay.id);
        if (outcome === 'started') {
          addNotification({
            type: 'info',
            title: 'Baking the plan at full resolution',
            message: 'Placement came from your anchors — this takes a few minutes; the plan stays usable meanwhile.',
          });
        }
      } catch (err) {
        addNotification({
          type: 'warning',
          title: 'Auto-bake could not start',
          message: `${err instanceof Error ? err.message : 'Unknown error'} — you can bake manually from the Plans Layout ticket.`,
        });
      }
      // Third surface: a DXF attached BEFORE anchoring fitted against an
      // unanchored (or stale) layout — Done re-aligns it so "click Done and
      // all is completed" is true for plan, labels AND vector layers. A DXF
      // still ingesting is fine: the poller fits at ready with the transform
      // that now exists.
      const withDxf = useProjectStore.getState().data.overlays.find((o) => o.id === overlay.id);
      if (withDxf?.dxf?.status === 'ready') {
        try {
          const fit = await refitDxfRegistration(withDxf);
          if (fit) {
            addNotification({
              type: 'success',
              title: 'DXF aligned to layout',
              message: `${fit.matched} matched labels (±${fit.rmsPt} pt).`,
            });
          }
        } catch (err) {
          console.warn('[AnchorMode] DXF re-align failed:', err);
        }
      }
    })();
  }, [setLayoutAnchorMode, addNotification]);

  // ── Convert anchor PDF coords to screen coords ──

  const anchorScreenPositions = useMemo(() => {
    if (!pageImage) return [];
    return anchors.map((a, i) => {
      const imgX = (a.pdfX / pageImage.pageWidthPt) * pageImage.widthPx;
      const imgY = (1 - a.pdfY / pageImage.pageHeightPt) * pageImage.heightPx;
      return {
        id: a.id,
        num: i + 1,
        screenX: imgX * zoom + pan.x,
        screenY: imgY * zoom + pan.y,
        residual: siteLayout?.transform?.anchorResiduals.find((r) => r.anchorId === a.id),
      };
    });
  }, [anchors, pageImage, zoom, pan, siteLayout?.transform]);

  // ── Pending point screen position ──

  const pendingScreen = useMemo(() => {
    if (!pendingPdfPoint || !pageImage) return null;
    const imgX = (pendingPdfPoint.x / pageImage.pageWidthPt) * pageImage.widthPx;
    const imgY = (1 - pendingPdfPoint.y / pageImage.pageHeightPt) * pageImage.heightPx;
    return { x: imgX * zoom + pan.x, y: imgY * zoom + pan.y };
  }, [pendingPdfPoint, pageImage, zoom, pan]);

  if (!siteLayout || !pageImage) return null;

  return (
    <div className="anchor-mode">
      {/* Control bar */}
      <div className="anchor-mode__bar">
        <div className="anchor-mode__instructions">
          {waitingForMapClick
            ? 'Now click the same point on the map →'
            : 'Click a recognizable feature on the PDF ←'}
        </div>
        <div className="anchor-mode__info">
          <span className="anchor-mode__count">{anchors.length} anchors</span>
          {siteLayout.transform && (
            <span className={`anchor-mode__rmse anchor-mode__rmse--${
              siteLayout.transform.residualMeters < 5 ? 'good'
                : siteLayout.transform.residualMeters < 20 ? 'fair'
                : 'poor'
            }`}>
              RMSE: {siteLayout.transform.residualMeters.toFixed(1)}m
            </span>
          )}
        </div>
        <button className="anchor-mode__done" onClick={handleDone} type="button">
          Done
        </button>
      </div>

      {/* PDF viewer */}
      <div
        ref={canvasRef}
        className="anchor-mode__pdf"
        onMouseDown={handleMouseDown}
        onClick={handlePdfClick}
        onWheel={handleWheel}
      >
        <div
          className="anchor-mode__pdf-content"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
          }}
        >
          <img
            ref={imgRef}
            src={pageImage.url}
            alt="Layout PDF"
            draggable={false}
            style={{ display: 'block' }}
          />
        </div>

        {/* Anchor markers on PDF */}
        {anchorScreenPositions.map((a) => (
          <div
            key={a.id}
            className="anchor-mode__marker anchor-mode__marker--placed"
            style={{ left: a.screenX, top: a.screenY }}
            title={`Anchor ${a.num}${a.residual ? ` (${a.residual.errorMeters.toFixed(1)}m)` : ''}`}
          >
            <span>{a.num}</span>
            <button
              className="anchor-mode__marker-delete"
              onClick={(e) => { e.stopPropagation(); handleDeleteAnchor(a.id); }}
              type="button"
              title="Remove anchor"
            >
              ×
            </button>
          </div>
        ))}

        {/* Pending crosshair */}
        {pendingScreen && (
          <div
            className="anchor-mode__marker anchor-mode__marker--pending"
            style={{ left: pendingScreen.x, top: pendingScreen.y }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24">
              <line x1="12" y1="0" x2="12" y2="24" stroke="#ef4444" strokeWidth="2" />
              <line x1="0" y1="12" x2="24" y2="12" stroke="#ef4444" strokeWidth="2" />
              <circle cx="12" cy="12" r="6" fill="none" stroke="#ef4444" strokeWidth="2" />
            </svg>
          </div>
        )}

        {/* Map-click prompt overlay */}
        {waitingForMapClick && (
          <div className="anchor-mode__prompt">
            Click the corresponding point on the map →
          </div>
        )}
      </div>

      {/* Anchor list */}
      <div className="anchor-mode__list">
        <div className="anchor-mode__list-header">Anchors</div>
        {anchors.length === 0 && (
          <div className="anchor-mode__list-empty">
            No anchors yet. Click points on both the PDF and map to create anchor pairs.
          </div>
        )}
        {anchors.map((a, i) => {
          const residual = siteLayout.transform?.anchorResiduals.find((r) => r.anchorId === a.id);
          return (
            <div key={a.id} className="anchor-mode__list-item">
              <span className="anchor-mode__list-num">{i + 1}</span>
              <span className="anchor-mode__list-coords">
                PDF: ({a.pdfX.toFixed(0)}, {a.pdfY.toFixed(0)})
              </span>
              <span className="anchor-mode__list-coords">
                Map: ({a.mapLng.toFixed(5)}, {a.mapLat.toFixed(5)})
              </span>
              {residual && (
                <span className={`anchor-mode__list-error anchor-mode__list-error--${
                  residual.errorMeters < 5 ? 'good' : residual.errorMeters < 20 ? 'fair' : 'poor'
                }`}>
                  {residual.errorMeters.toFixed(1)}m
                </span>
              )}
              <button
                className="anchor-mode__list-delete"
                onClick={() => handleDeleteAnchor(a.id)}
                type="button"
                title="Remove"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
